/**
 * The Skills pane — item BD, phase 1.
 *
 * `bimgo-ai-web`'s saved-skills list, two tabs wide: the local store ("My
 * Skills": its folders, then the skills at the root, in one list) and what the
 * organization shares. Opening a card swaps the list for the detail. Design
 * §9, *The Skills pane*.
 *
 * **One scope, and it is the instance in the instance card.** The vendor is the
 * first half of the heading; the year beside it is read-only, because it is the
 * year of the instance picked in the footer. The reference keyed everything on
 * the agent's one `revitVersion`, and a later pass here made the year a filter
 * of the pane's own — which let the pane browse 2021 while the footer drove a
 * 2024 document, and listed skills that could not be run. With no Revit running
 * there is no year at all, and the pane asks for one to be opened rather than
 * showing a list.
 *
 * **AutoCAD looks and behaves as Revit does** (owner, 2026-10-24): the same tabs, scope,
 * cards and detail. Its skills live in the vendor store, one skill holding a folder per
 * year (docs/proposals/workspaces.md, stage 1). The agent lists a row per year folder, and
 * the list shows the selected AutoCAD's year only — exactly as a Revit year store does — so
 * a skill appears once, and only when it supports the AutoCAD in use (owner, 2026-10-24).
 * Its grouping folders are vendor-wide rather than per year.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { useSkills } from "../hooks/useSkills";
import { hasAction, type PermissionsState } from "../hooks/usePermissions";
import type { HostsView } from "../hooks/useHosts";
import { NO_SELECTION, selectionFor, type InstanceRestriction, type Selection, type Selections } from "../lib/selectedInstance";
import { describeAgent, type AgentTarget } from "../lib/agentClient";
import {
  atVersion,
  DEFAULT_SKILL_SORT,
  isHostKind,
  describeTarget,
  HOST_KINDS,
  VENDOR_NAMES,
  filterSkills,
  foldersForVendor,
  groupSkills,
  paginate,
  SKILLS_PAGE_SIZE,
  connectedVersionsForVendor,
  scopeVersion,
  skillKey,
  skillsForVendor,
  sortSkills,
  versionsForVendor,
  type Skill,
  type SkillFolder,
  type FolderPick,
  type SkillSort,
  type VendorPick,
} from "../lib/skills";
import { Modal } from "../components/settings/Modal";
import { Toast } from "../components/Toast";
import { FolderCard, SkillCard } from "../components/skills/SkillCard";
import { ListFoot } from "../components/skills/Pager";
import { ALL_VERSIONS, SortSelect, VersionScope } from "../components/skills/ListControls";
import type { SkillEditSessions } from "../lib/skillEditSessions";
import { SkillDetail } from "../components/skills/SkillDetail";
import { OrgSkillsList } from "../components/skills/OrgSkillsList";
import { useCloudSkills } from "../hooks/useCloudSkills";
import type { CloudSkill } from "../lib/skills";
import { AlertIcon, AutocadGlyph, ChevronLeftIcon, FolderPlusIcon, InfoIcon, LoaderIcon, RevitGlyph, SearchIcon, WrenchIcon } from "../components/skills/icons";

export default function SkillsPane({
  agent,
  hosts,
  selection,
  selections,
  orgId,
  token,
  userId,
  projectId,
  perms,
  skillEdits,
  openSkill,
  onOpenSkillHandled,
  onInstanceRestriction,
}: {
  /** Narrow a vendor's instance picker — to an open AutoCAD skill's supported years. */
  onInstanceRestriction?: (restriction: InstanceRestriction | null) => void;
  /** The request above has been acted on — the shell drops it, so it opens once. */
  onOpenSkillHandled?: () => void;
  /** Every skill's Edit-with-AI conversation, held by the shell (#196). */
  skillEdits: SkillEditSessions;
  /** A skill to open, asked for from outside the pane — the sidebar's Running list. */
  openSkill?: { id: string } | null;
  agent: AgentTarget;
  hosts: HostsView;
  /** The primary selection — what the tree lands on. */
  selection: Selection;
  /** Every vendor's selection (#5): a skill runs and edits against its own vendor's. */
  selections: Selections;
  orgId: string | null;
  token: string | null;
  userId: string | null;
  projectId: string | null;
  perms: PermissionsState;
}) {
  const { t } = useLocale();
  const skills = useSkills(orgId, token);
  const instances = hosts.kind === "loaded" ? hosts.instances : [];

  /**
   * The vendor the user chose, if any — resolved into `pick` below.
   *
   * Held apart from the resolved value because a choice can stop being valid: the vendor's
   * last instance closes and its store is empty, and the groups no longer hold it.
   */
  const [vendorPick, setVendorPick] = useState<VendorPick | null>(null);
  /**
   * The open folder, as a **name and a year** (`FolderPick`).
   *
   * A name alone was ambiguous once the list spanned years: "Export" exists as its own
   * directory in 2024 and in 2025, and opening one has to show that one's skills.
   */
  const [folder, setFolder] = useState<FolderPick | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  /**
   * Cards per page: a reading preference, not a position, so it survives opening a folder
   * and switching tabs. The page number is the part that does not travel.
   */
  const [perPage, setPerPage] = useState(SKILLS_PAGE_SIZE);
  const [sort, setSort] = useState<SkillSort>(DEFAULT_SKILL_SORT);
  const [openId, setOpenId] = useState<string | null>(null);
  // A Running row reopens a skill whose edit carries on in the background (#196).
  useEffect(() => {
    if (!openSkill) return;
    setOpenId(openSkill.id);
    // Inside its vendor's page, so leaving the skill returns to that vendor's list.
    setVendorPick({ kind: openSkill.id.startsWith("autocad:") ? "autocad" : "revit" });
    onOpenSkillHandled?.();
  }, [openSkill, onOpenSkillHandled]);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  /**
   * Which year store a new folder is created in — **the scope year**, captured when the
   * button opens the modal.
   *
   * A folder is one directory inside one year's store, so the agent still has to be told
   * which. It stopped being a question the moment the pane was scoped to a single year:
   * there is exactly one answer, and it is the one on the title row.
   */
  const [newFolderVersion, setNewFolderVersion] = useState("");
  const [creating, setCreating] = useState(false);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  // The reference's two tabs: the local store, and what the organization shares.
  const [tab, setTab] = useState<"saved" | "shared">("saved");
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [deletingCloudIds, setDeletingCloudIds] = useState<Set<string>>(new Set());
  const mayRun = hasAction(perms, "skill:run");
  const mayToggle = hasAction(perms, "skill:enable");
  const mayDelete = hasAction(perms, "skill:delete");
  const mayEdit = hasAction(perms, "skill:edit");
  const mayPublish = hasAction(perms, "skill:publish");

  const loaded = skills.state.kind === "loaded" ? skills.state : null;
  const groups = useMemo(() => groupSkills(loaded?.skills ?? [], instances), [loaded, instances]);

  /**
   * **One Skills page, the vendor chosen by tabs beside the title** (owner, 2026-10-24):
   * [Revit] [AutoCAD Beta]. A tab is enabled once an instance of that vendor is connected.
   * The vendor in view is the user's choice while it is connected, else the selected
   * instance's vendor, else the first connected one; with nothing connected there is none,
   * and the page asks for software to be opened.
   */
  const connectedKinds = useMemo(
    () => new Set(instances.map((instance) => instance.host_kind).filter(isHostKind)),
    [instances],
  );
  const selectedKind = selection.instance && isHostKind(selection.instance.host_kind) ? selection.instance.host_kind : null;
  const activeKind: VendorPick["kind"] | null =
    vendorPick && connectedKinds.has(vendorPick.kind)
      ? vendorPick.kind
      : selectedKind && connectedKinds.has(selectedKind)
        ? selectedKind
        : (HOST_KINDS.find((kind) => connectedKinds.has(kind)) ?? null);
  const pick: VendorPick = { kind: activeKind ?? "revit" };
  const onVendorPage = activeKind !== null;
  const chooseVendor = (kind: VendorPick["kind"]) => {
    if (kind === activeKind) return;
    setVendorPick({ kind });
    setFolder(null);
    setQuery("");
    // AutoCAD has no Organization tab (beta), so switching vendor lands on My Skills.
    setTab("saved");
  };
  /**
   * The vendor switch beside the title: a pill per **connected** vendor, its glyph and name,
   * AutoCAD's carrying **Beta** — its skills ship on the new store (one workspace and one git
   * history per skill, a folder per AutoCAD year). Only vendors with a connected instance are
   * offered, and with none connected there is no switch at all (owner, 2026-10-24).
   */
  const connectedVendors = HOST_KINDS.filter((kind) => connectedKinds.has(kind));
  const vendorTabs =
    connectedVendors.length === 0 ? null : (
      <div className="skills-vendor-tabs" role="tablist" aria-label={t("skills.vendors.label")}>
        {connectedVendors.map((kind) => {
          const active = activeKind === kind;
          return (
            <button
              key={kind}
              type="button"
              role="tab"
              aria-selected={active}
              className={`skills-vendor-tab${active ? " is-active" : ""}`}
              data-vendor={kind}
              onClick={() => chooseVendor(kind)}
            >
              <span className="skills-vendor-glyph" aria-hidden="true">
                {kind === "revit" ? <RevitGlyph size={14} /> : <AutocadGlyph size={14} />}
              </span>
              {VENDOR_NAMES[kind]}
              {kind === "autocad" ? <span className="app-beta-badge">Beta</span> : null}
            </button>
          );
        })}
      </div>
    );

  /**
   * **The year the pane is scoped to, and the instance that decides it.**
   *
   * This vendor's own selection (#5) — the one named in the instance card — rather than the
   * primary one, so an AutoCAD pane is scoped by the AutoCAD pick even while a Revit is up.
   * `scopeVersion` is empty when nothing of this vendor is running, which is the state the
   * pane answers with `skills.noInstance` rather than with a list.
   */
  const vendorSelection = pick ? selectionFor(selections, pick.kind) : NO_SELECTION;
  const versionFilter = scopeVersion(vendorSelection);

  /**
   * The shared list, narrowed **server-side** by the year (owner).
   *
   * api-v2 takes `revit_version` and its handler resolves each distinct publisher with its own
   * admin round-trip, so asking for one year is cheaper than asking for all of them and throwing
   * the rest away. `ALL_VERSIONS` sends no parameter and gets everything — which now only
   * happens while nothing is connected, where the pane shows no list anyway.
   */
  // Not for AutoCAD: sharing its skills waits for the cloud side of the new store (owner,
  // 2026-10-24), so its Organization tab is hidden and nothing is fetched for it.
  const cloud = useCloudSkills(skills.client, pick.kind !== "autocad", pick, versionFilter);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 2500);
    return () => window.clearTimeout(id);
  }, [notice]);

  /** Every version's skills for the selected vendor — the year is a card badge now. */
  const isAutocad = pick.kind === "autocad";
  const inVendor = useMemo(() => skillsForVendor(groups, pick), [groups, pick]);
  /** The years this vendor holds: the tree subtitle, and what "New folder" may target. */
  const vendorVersions = useMemo(() => versionsForVendor(groups, pick), [groups, pick]);
  /** Of those, the ones with an instance open — marked in the filter's options. */
  const runningVersions = useMemo(() => connectedVersionsForVendor(groups, pick), [groups, pick]);
  /**
   * No union of local and shared years any more, and no accumulation of what the
   * organization has published.
   *
   * Both existed to fill a filter the user drove: the control had to offer a year the
   * organization shares but this machine has nothing for, and — once the shared fetch was
   * narrowed server-side — it had to remember the years an earlier, wider response had
   * revealed, or choosing one would delete the rest from the control that chose it. The
   * scope comes from a running instance now, so there is one year and nothing to offer.
   */

  /**
   * Folders in the **scope year** — the selected instance's.
   *
   * On disk a folder belongs to one year, so "Export" in 2024 and 2025 is two directories.
   * Following the scope is what keeps the two halves of My Skills agreeing: with 2024 in
   * play the list showed only 2024's skills while the folder row above still counted
   * 2025's, so the folder claimed work the list did not contain (owner).
   */
  const scopedFolders = useMemo(
    () =>
      // An AutoCAD folder is vendor-wide (the year is inside each skill), so every one shows.
      isAutocad
        ? (loaded?.folders ?? []).filter((f) => f.kind === "autocad").sort((a, b) => a.name.localeCompare(b.name))
        : foldersForVendor(
            (loaded?.folders ?? []).filter((f) => f.kind !== "autocad"),
            versionFilter === ALL_VERSIONS ? vendorVersions : [versionFilter],
          ),
    [loaded, vendorVersions, versionFilter, isAutocad],
  );
  const visibleSkills = useMemo(() => {
    /**
     * Inside a folder, that folder's skills. Otherwise **the skills at the root** — the ones
     * not filed into a folder anyone made.
     *
     * That is the split the two bookmarks name (owner): Folders is where the filed work is,
     * Skills is what is loose at the top level. A flat list of everything would make the
     * two views overlap, so a skill would appear under both and neither count would mean
     * anything.
     *
     * Both halves of the folder pick are matched, or opening 2024's "Export" would also
     * list 2025's.
     */
    const inPlace = folder
      ? inVendor.filter((s) => s.parentFolder === folder.name && (isAutocad || s.target.version === folder.version))
      : inVendor.filter((s) => (s.parentFolder ?? null) === null);
    // The scope year's rows only, for both vendors: an AutoCAD skill without that year's
    // folder is not listed, as a Revit skill in another year store is not.
    const inYear = atVersion(inPlace, versionFilter, (s) => s.target.version);
    // `sortSkills` replaces the inline created-descending sort this had; that ordering is
    // still the default, it is just no longer the only one.
    return sortSkills(filterSkills(inYear, query), sort, {
      version: (s) => s.target.version,
      created: (s) => s.createdAt,
      // No `updated`: a local skill records only when it was created (see `SkillSort`).
      name: (s) => s.displayName,
    });
  }, [inVendor, folder, query, versionFilter, sort]);
  /**
   * Folder cards obey the sort control too (owner), through the same helper the skills use.
   *
   * A folder has **no timestamp of its own** — `SkillFolder` is a name, a count and a year
   * — so the date comes from what is inside it: the newest skill it holds under "Newest
   * first", the oldest under "Oldest first". That is the only reading that makes the
   * control mean anything for a folder, and it matches what a user is asking for when they
   * sort by recency: show me where the recent work is.
   *
   * An empty folder has no date at all and sorts as though it were the oldest — last under
   * "Newest first", first under "Oldest first" — with the name as the tie-break either way.
   */
  const visibleFolders = useMemo(() => {
    if (folder) return [];
    const q = query.trim().toLowerCase();
    const matching = q ? scopedFolders.filter((f) => f.name.toLowerCase().includes(q)) : scopedFolders;

    const dates = new Map<string, { newest: string; oldest: string }>();
    for (const skill of inVendor) {
      if (!skill.parentFolder) continue;
      const key = `${isAutocad ? "" : skill.target.version}/${skill.parentFolder}`;
      const seen = dates.get(key);
      if (!seen) {
        dates.set(key, { newest: skill.createdAt, oldest: skill.createdAt });
        continue;
      }
      if (Date.parse(skill.createdAt) > Date.parse(seen.newest)) seen.newest = skill.createdAt;
      if (Date.parse(skill.createdAt) < Date.parse(seen.oldest)) seen.oldest = skill.createdAt;
    }

    return sortSkills(matching, sort, {
      version: (f) => f.version,
      created: (f) => {
        const at = dates.get(`${f.version}/${f.name}`);
        if (!at) return "";
        return sort === "oldest" ? at.oldest : at.newest;
      },
      name: (f) => f.name,
    });
  }, [folder, query, scopedFolders, inVendor, sort, isAutocad]);
  /**
   * My Skills as **one list: the folders first, then the skills at the root**, the way 1.0
   * shows it (owner, 2026-10-22). It used to be two views chosen from a bookmark rail, which
   * hid the folders behind a control nobody went looking for.
   *
   * One pager over both, so a store with forty folders still pages rather than pushing the
   * skills off the screen. `paginate` clamps, so a list that shrinks under the remembered page
   * (a search, a delete) shows the last page rather than nothing. Inside a folder there are no
   * folders, and the list is that folder's skills.
   */
  const entries = useMemo<Array<{ kind: "folder"; folder: SkillFolder } | { kind: "skill"; skill: Skill }>>(
    () => [
      ...visibleFolders.map((f) => ({ kind: "folder" as const, folder: f })),
      ...visibleSkills.map((skill) => ({ kind: "skill" as const, skill })),
    ],
    [visibleFolders, visibleSkills],
  );
  const paged = useMemo(() => paginate(entries, page, perPage), [entries, page, perPage]);

  /**
   * Back to page 1 when the *scope* changes, which clamping alone does not cover:
   * page 3 of another folder, year or search is disorienting even when it exists.
   */
  useEffect(() => {
    setPage(1);
  }, [folder?.name, folder?.version, query, tab, versionFilter, pick?.kind, perPage]);

  /**
   * Switching tab clears the search **and the open folder**: both are positions inside My
   * Skills, and carried to Organization they would filter a list they do not belong to.
   */
  useEffect(() => {
    setQuery("");
    setFolder(null);
  }, [tab]);


  /**
   * New folder, on My Skills' search row as in 1.0, beside the list it adds to.
   *
   * Gated on a vendor with a year store to create in, and hidden inside a folder, where there
   * is nothing to add a folder to.
   */
  const newFolderAction =
    tab === "saved" && folder === null && versionFilter !== "" ? (
      <button
        type="button"
        className="settings-btn secondary is-small"
        onClick={() => {
          /**
           * **The scope year, always** — the year of the instance named in the instance card.
           *
           * This used to offer a choice, defaulting to the newest year the vendor held. With
           * the pane scoped to one year that choice can only misfire: a folder made in 2021
           * while the pane is showing 2024 is created, reported as created, and then not
           * there — it lives in a store the pane no longer displays. The modal's year select
           * goes with it (see the `Modal` below).
           */
          setNewFolderVersion(versionFilter);
          setNewFolderOpen(true);
        }}
      >
        <FolderPlusIcon size={14} /> {t("skills.newFolder")}
      </button>
    ) : null;

  const open = openId ? (loaded?.skills.find((s) => s.id === openId) ?? null) : null;

  /**
   * **An open AutoCAD skill decides which AutoCAD years can be selected** (owner,
   * 2026-10-24): only the ones it supports. Switching to another would leave it open where
   * it can neither run nor be edited; a cross-version build, started from a supported year,
   * is how a year is added. Lifted when the skill closes or the pane goes away.
   */
  const openSupported = open?.target.kind === "autocad" ? (open.versionFolders ?? [open.target.version]).join(",") : null;
  useEffect(() => {
    onInstanceRestriction?.(openSupported === null ? null : { kind: "autocad", versions: openSupported.split(",") });
  }, [openSupported, onInstanceRestriction]);
  useEffect(() => () => onInstanceRestriction?.(null), [onInstanceRestriction]);

  /**
   * The open AutoCAD skill follows the selected AutoCAD: switching to another supported
   * year in the title bar reopens the skill at that year's folder, so what runs and what
   * the edit chat changes is always the year in use.
   */
  const selectedAutocadYear = scopeVersion(selectionFor(selections, "autocad"));
  useEffect(() => {
    if (!open || open.target.kind !== "autocad") return;
    if (!selectedAutocadYear || open.target.version === selectedAutocadYear) return;
    if (!(open.versionFolders ?? []).includes(selectedAutocadYear)) return;
    setOpenId(skillKey(open.baseId, selectedAutocadYear));
  }, [open, selectedAutocadYear]);

  const mark = (id: string, on: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const setIn = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string, on: boolean) =>
    setter((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const download = async (cloudSkill: CloudSkill) => {
    if (!skills.client) return;
    setIn(setDownloadingIds, cloudSkill.id, true);
    try {
      // No pane year to offer any more — the tree selects a vendor. The agent already
      // preferred the skill's own `revitVersion` over this fallback, so the only case
      // that changes is a cloud skill with no year at all, which the agent refuses
      // rather than filing under whatever happened to be selected.
      await skills.client.download(cloudSkill.id, null);
      // The copy now lives under its year in My Skills — reload so "Saved"
      // flips here and the card appears there.
      skills.reload();
      setNotice({ tone: "ok", text: t("skills.downloadSuccess") });
    } catch (error) {
      setNotice({ tone: "err", text: `${t("skills.downloadError")}: ${error instanceof Error ? error.message : String(error)}` });
    } finally {
      setIn(setDownloadingIds, cloudSkill.id, false);
    }
  };

  const deleteCloud = async (cloudSkill: CloudSkill) => {
    if (!skills.client) return;
    setIn(setDeletingCloudIds, cloudSkill.id, true);
    try {
      await skills.client.removeCloud(cloudSkill.id);
      cloud.reload();
      // The local copy's "Published" badge clears with the agent's reset.
      skills.reload();
      setNotice({ tone: "ok", text: t("skills.deleteSuccess") });
    } catch (error) {
      setNotice({ tone: "err", text: `${t("skills.deleteError")}: ${error instanceof Error ? error.message : String(error)}` });
    } finally {
      setIn(setDeletingCloudIds, cloudSkill.id, false);
    }
  };

  const move = async (skill: Skill, folderName: string | null) => {
    if (skill.parentFolder === folderName) return;
    mark(skill.id, true);
    try {
      await skills.move(skill.id, folderName);
      setNotice({ tone: "ok", text: t("skills.moveSuccess") });
    } catch (error) {
      setNotice({ tone: "err", text: error instanceof Error ? error.message : t("skills.moveError") });
    } finally {
      mark(skill.id, false);
    }
  };

  const toggle = async (skill: Skill, enabled: boolean) => {
    mark(skill.id, true);
    try {
      await skills.setEnabled(skill.id, enabled);
      setNotice({ tone: "ok", text: t(enabled ? "toolEditor.enableSuccess" : "toolEditor.disableSuccess") });
    } catch (error) {
      setNotice({ tone: "err", text: error instanceof Error ? error.message : t("toolEditor.toggleEnabledError") });
    } finally {
      mark(skill.id, false);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim() || !pick) return;
    setCreating(true);
    setFolderError(null);
    try {
      await skills.createFolder(newFolderName, isAutocad ? "" : newFolderVersion, isAutocad ? "autocad" : undefined);
      setNewFolderName("");
      setNewFolderOpen(false);
      // Show the folder that was just made: My Skills, at the root, whichever tab New folder
      // was pressed from. From Organization it would otherwise report success over a list
      // that cannot contain it.
      setTab("saved");
      setFolder(null);
      setNotice({ tone: "ok", text: t("skills.folderCreateSuccess") });
    } catch (error) {
      setFolderError(error instanceof Error ? error.message : t("skills.folderCreateError"));
    } finally {
      setCreating(false);
    }
  };

  // --- states before the list ---------------------------------------------------

  if (!agent.ok) {
    return (
      <div className="skills-pane">
        <PaneHead t={t} />
        <div className="settings-card skills-empty">
          <span className="skills-empty-glyph"><AlertIcon size={22} /></span>
          <p className="skills-empty-title">{t("skills.agentUnavailable")}</p>
          <p className="skills-empty-desc">{describeAgent(agent)}</p>
        </div>
      </div>
    );
  }

  if (skills.state.kind === "idle" || skills.state.kind === "loading") {
    return (
      <div className="skills-pane">
        <PaneHead t={t} />
        <div className="skills-center muted"><LoaderIcon size={14} /> {t("common.loading")}</div>
      </div>
    );
  }

  if (skills.state.kind === "error") {
    const outdated = skills.state.status === 404;
    return (
      <div className="skills-pane">
        <PaneHead t={t} />
        <div className="settings-card skills-empty">
          <span className="skills-empty-glyph is-warn"><AlertIcon size={22} /></span>
          <p className="skills-empty-title">{t(outdated ? "skills.pluginOutdated" : "skills.fetchError")}</p>
          <p className="skills-empty-desc">{outdated ? t("skills.pluginOutdatedDesc") : skills.state.message}</p>
          <button type="button" className="settings-btn secondary" onClick={skills.reload}>{t("plugins.retry")}</button>
        </div>
      </div>
    );
  }

  // --- the detail -----------------------------------------------------------------

  if (open && skills.client) {
    return (
      <div className="skills-pane is-editor">
        <SkillDetail
          key={open.id}
          skill={open}
          skillEdits={skillEdits}
          client={skills.client}
          // The skill's own vendor's selection (D8): an AutoCAD skill is gated
          // against the AutoCAD pick, never against whichever Revit is up.
          selection={selectionFor(selections, open.target.kind)}
          orgId={orgId}
          token={token}
          userId={userId}
          projectId={projectId}
          mayRun={mayRun}
          mayDelete={mayDelete}
          mayEdit={mayEdit}
          mayPublish={mayPublish}
          onBack={() => setOpenId(null)}
          onDelete={async () => {
            await skills.remove(open.id);
            setOpenId(null);
            setNotice({ tone: "ok", text: t("toast.skillDeleteSuccess") });
          }}
          onRan={() => skills.reload()}
          onChanged={() => skills.reload()}
          // A version switch removed the AutoCAD year open now: reopen at a year it has.
          onReopen={async (key) => {
            await skills.refresh();
            setOpenId(key);
          }}
          /**
           * A publish adds a row to what the organization shares, and that list was fetched
           * before the row existed — so it is refetched here (owner). `onChanged` cannot carry
           * this: it also fires for a rename and for every commit, and this is the fetch that
           * costs api-v2 one admin round-trip per distinct publisher.
           */
          onPublished={cloud.reload}
        />
      </div>
    );
  }

  // --- nothing to be scoped to ----------------------------------------------------

  /**
   * **No instance of this vendor is running, so there is no year and no list** (owner,
   * 2026-09-20).
   *
   * The pane's scope is the instance picked in the instance card, and a skill is stored, edited
   * and run against one. With nothing open there is no year to show, every card would be
   * one that cannot run, and the read-only year pill would have nothing to read. So the pane
   * says what to do about it instead.
   *
   * **The message is vendor-neutral** ("your design software", naming Revit and AutoCAD
   * as examples) now that AutoCAD skills exist (owner, 2026-10-24). It used to name Revit
   * alone, while beta served Revit only.
   *
   * This also stands in for the old "No Skills Generated Yet" card, which could only be
   * reached with nothing connected *and* an empty store: a running instance always puts its
   * year in `groups`, so `groups` is empty only when this state already holds. One message
   * rather than two for the same situation, and this is the actionable one.
   *
   * Placed **after** the detail, so a skill left open when the last instance closes stays open
   * and readable — its own run gate already refuses to execute it.
   */
  if (!onVendorPage || !versionFilter) {
    // With no vendor connected the page asks for software to be opened; the vendor tabs
    // stay in the heading, disabled until something is (owner, 2026-10-24).
    return (
      <div className="skills-pane">
        <PaneHead t={t} tabs={vendorTabs} />
        <div className="settings-card skills-empty">
          <span className="skills-empty-glyph"><WrenchIcon size={22} /></span>
          <p className="skills-empty-title">{t("skills.noInstance")}</p>
          {/* Two paragraphs (owner, 2026-10-24): how skills are organised, then what to do
              about it — the instruction is the line the user acts on, so it gets its own. The
              card's text is wide enough that each stays on one line. */}
          <p className="skills-empty-desc">{t("skills.noInstanceDesc")}</p>
          <p className="skills-empty-desc">{t("skills.noInstanceHint")}</p>
        </div>
      </div>
    );
  }

  // --- the list ------------------------------------------------------------------

  return (
    /**
     * `is-browser` makes the height chain definite from `.app-content` down, the way
     * `is-editor` already does, so the cards scroll inside their own column and the
     * tabs and the search row stay put (owner). Without the marker class the pane
     * would have to be matched by exclusion, which would also catch the loading,
     * error and empty states — short cards that want the page's own scroll.
     */
    <div className="skills-pane is-browser">
      <PaneHead
        t={t}
        tabs={vendorTabs}
        /**
         * The scope year and the order live **on the title row** (owner), not in each
         * list's head.
         *
         * Each of the three views held its own pair, so narrowing to 2024 in My Skills and
         * stepping over to Folders or Organization asked the same question again. Both are a
         * lens on the vendor named beside them rather than a property of one list, and the
         * title row is where the vendor is — so one pair up here answers for all three and
         * keeps its answer as you move between them.
         *
         * The year is no longer a question either way: it reports the instance picked in
         * the instance card, read-only, and sits beside the vendor it belongs to.
         */
        actions={
          <>
            <SortSelect value={sort} onChange={setSort} />
            <VersionScope connected={runningVersions} value={versionFilter} />
          </>
        }
      />
      {/* The same toast the editor uses: a move, a download or a new folder is confirmed at
          the corner of the window rather than by a row appearing under the heading and
          shifting the cards down. */}
      {notice ? <Toast tone={notice.tone} text={notice.text} onClose={() => setNotice(null)} /> : null}

      {/* No left column any more (owner): the vendor is a control on the title row, so
          the list takes the full width. `.skills-split` and its 200px track are gone
          with the tree they existed for. */}
      <section className="skills-list">
        {/* The reference's two tabs: the local store, and what the organization
            shares. Both answer for the vendor selected on the title row. */}
        <div className="skill-tabs skills-pane-tabs" role="tablist">
          {/* AutoCAD has My Skills only while its new store is in beta (owner, 2026-10-24). */}
          {((isAutocad
            ? [["saved", t("skills.mySkills")]]
            : [["saved", t("skills.mySkills")], ["shared", t("skills.sharedTab")]]) as Array<["saved" | "shared", string]>).map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className={`skill-tab${tab === id ? " is-active" : ""}`} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>

        {tab === "shared" && !isAutocad ? (
          <OrgSkillsList
            state={cloud.state}
            refreshing={cloud.refreshing}
            pick={pick}
            localSkills={loaded?.skills ?? []}
            versionFilter={versionFilter}
            sort={sort}
            userId={userId}
            mayDelete={mayDelete}
            downloading={downloadingIds}
            deleting={deletingCloudIds}
            onDownload={(skill) => void download(skill)}
            onDelete={(skill) => void deleteCloud(skill)}
            onRetry={cloud.reload}
          />
        ) : (
        <div className="skills-body">
        {/**
         * Two rows, when inside a folder: **where you are, then what you can do**
         * (owner).
         *
         * The path was the first item of the controls row, which put a navigation control
         * in a row of filters — it pushed the search field sideways as you entered and
         * left a folder, and at a narrow width it wrapped in among the pills. On its own
         * row above them it reads as the heading of the list it describes, and the
         * controls row keeps one shape whether or not a folder is open.
         */}
        {folder ? (
          <div className="skills-crumb-row">
            <button type="button" className="link-btn skills-crumb" onClick={() => { setFolder(null); setQuery(""); }}>
              <ChevronLeftIcon size={14} />
              <span className="muted">{t("skills.mySkills")}</span>
              <span className="sep">/</span>
              <span className="skills-crumb-name">{folder.name}</span>
              {/* The year, because two folders can share a name — the crumb has to say
                  which one you are in. */}
              <span className="skills-crumb-version">{folder.version}</span>
            </button>
          </div>
        ) : null}
        {/* The controls that act on the list: the search taking the slack, then New
            folder (at the root only). No title: the tab above already says what this
            list is. */}
        <div className="skills-list-head">
          <div className="search-field">
            <span className="search-field-icon"><SearchIcon size={14} /></span>
            <input className="settings-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("skills.searchPlaceholder")} />
          </div>
          {newFolderAction}
        </div>

        {/* Everything below the head scrolls; the head and the tabs above it do not.
            One grid: the folders first, then the skills, or a folder's skills once one
            is open. */}
        <div className="skills-scroll">
        {paged.total === 0 ? (
          <div className="skills-dashed">
            {/* No "this vendor is not connected" arm: the pane does not reach a list in
                that state. It asks for Revit to be opened instead. */}
            {query.trim()
              ? t("skills.noMatch")
              : folder
                ? t("skills.folderEmpty")
                : t("skills.noSkillsForVersion")}
          </div>
        ) : (
          <div className="skills-grid">
            {paged.items.map((entry) =>
              entry.kind === "folder" ? (
                <FolderCard
                  key={`folder:${entry.folder.version}/${entry.folder.name}`}
                  folder={entry.folder}
                  onOpen={() => { setFolder({ name: entry.folder.name, version: entry.folder.version }); setQuery(""); }}
                />
              ) : (
                <SkillCard
                  key={entry.skill.id}
                  skill={entry.skill}
                  folders={(loaded?.folders ?? []).filter((f) =>
                    isAutocad ? f.kind === "autocad" : f.kind !== "autocad" && f.version === entry.skill.target.version,
                  )}
                  mayToggle={mayToggle}
                  busy={busyIds.has(entry.skill.id)}
                  onOpen={() => setOpenId(entry.skill.id)}
                  onMove={(name) => void move(entry.skill, name)}
                  onToggle={(enabled) => void toggle(entry.skill, enabled)}
                />
              ),
            )}
          </div>
        )}
        </div>
        {/* Outside `.skills-scroll`: the count and the pager stay put with the head, so
            both are readable without scrolling to the bottom of the page you are on. */}
        <ListFoot
          page={paged.page}
          pageCount={paged.pageCount}
          total={paged.total}
          counting={visibleFolders.length > 0 ? "items" : "skills"}
          perPage={perPage}
          onPerPage={setPerPage}
          onPage={setPage}
        />
        </div>
        )}
      </section>

      <Modal
        open={newFolderOpen}
        onClose={() => {
          if (creating) return;
          setNewFolderOpen(false);
          setNewFolderName("");
          setFolderError(null);
        }}
        title={t("skills.newFolder")}
        description={
          isAutocad
            ? t("skills.autocad.newFolderDesc")
            : t("skills.newFolderDesc").replace("{version}", describeTarget({ kind: pick.kind, version: versionFilter }))
        }
        footer={
          <>
            <button type="button" className="settings-btn secondary" disabled={creating} onClick={() => setNewFolderOpen(false)}>{t("common.cancel")}</button>
            <button type="button" className="settings-btn" form="new-skill-folder" disabled={creating || !newFolderName.trim()} onClick={() => void createFolder()}>
              {creating ? <LoaderIcon size={14} /> : null} {t("skills.createFolder")}
            </button>
          </>
        }
      >
        <form
          id="new-skill-folder"
          onSubmit={(e) => {
            e.preventDefault();
            void createFolder();
          }}
        >
          <input
            className="settings-input"
            autoFocus
            value={newFolderName}
            maxLength={120}
            placeholder={t("skills.folderNamePlaceholder")}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
          {/* No year select any more (owner, 2026-09-20). A folder is still one directory
              in one year's store, but the pane shows one year — the connected instance's —
              so a folder created in any other one would be reported as created and then be
              nowhere to be seen. The year is `versionFilter`, set when the button opens
              this, and the description below names it. */}
          {folderError ? <p className="field-error">{folderError}</p> : null}
        </form>
      </Modal>
    </div>
  );
}

// No refresh button (owner, 2026-08-25): the list reloads on every change it
// makes, and the pane re-reads when it opens.
function PaneHead({
  t,
  tabs,
  actions,
}: {
  t: (k: string) => string;
  /** The vendor tabs, beside the title (owner, 2026-10-24). */
  tabs?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="pane-head">
      <div className="pane-head-title">
        {/**
         * `Skills ⓘ [Revit] [AutoCAD Beta]` — the vendor is the tabs right beside the title
         * (owner, 2026-10-24), not part of the heading's words. The info glyph stays inside
         * the `h2`, against the word it explains, whatever `.pane-head-title`'s gap is.
         */}
        <h2 className="pane-head-h2">
          {t("skills.title")}
          {/* The one-line explainer lives behind a glyph rather than under the title
              (owner, 2026-08-24) — hover or focus to read it. */}
          <span className="info-tip" tabIndex={0} role="note" aria-label={t("skills.subtitle")}>
            <InfoIcon size={15} />
            <span className="info-tip-bubble" role="tooltip">{t("skills.subtitle")}</span>
          </span>
        </h2>
        {tabs}
      </div>
      {/* The right of the title row: the year filter and the order (owner). New folder is not
          here any more — it moved down to the Folders view, where folders are the subject,
          while these two answer for every view at once and so belong beside the vendor they
          are a lens on. */}
      {actions ? <div className="pane-head-actions">{actions}</div> : null}
    </div>
  );
}
